import { isUsernameAvailable } from '@/features/domain/user'
import { useValidateEmail } from '@/hooks'
import { store } from '@/store'
import { RmAccountFormValues, RmAccountValidation } from '../../types'
import { useTexts } from './useTexts'

const validateForm =
  (texts: ReturnType<typeof useTexts>, validateEmail: ReturnType<typeof useValidateEmail>) =>
  async (
    formValues: RmAccountFormValues,
    currentUserName: string,
  ): Promise<RmAccountValidation> => {
    const { name, email, surname, username } = formValues

    const validation: RmAccountValidation = {}

    if (!name || name.trim().length === 0) {
      validation.name = texts.nameRequired
    } else if (name.length > 30) {
      validation.name = texts.nameMaxLength
    }

    if (!email || email.trim().length === 0) {
      validation.email = texts.emailRequired
    } else {
      const isValidEmail = await validateEmail(email)
      if (!isValidEmail) {
        validation.email = texts.emailValid
      }
    }

    if (!surname || surname.trim().length === 0) {
      validation.surname = texts.surnameRequired
    } else if (surname.length > 30) {
      validation.surname = texts.surnameMaxLength
    }

    if (!username || username.trim().length === 0) {
      validation.username = texts.usernameRequired
    } else if (username.length < 6) {
      validation.username = texts.usernameMinLength
    } else if (username.length > 50) {
      validation.username = texts.usernameMaxLength
    } else if (/\s/.test(username.trim())) {
      validation.username = texts.allowedCharacters
    } else {
      const isChanged = username.trim() !== currentUserName.trim()
      if (isChanged) {
        try {
          const thunkResult = await store.dispatch(isUsernameAvailable(username.trim()))

          if (isUsernameAvailable.rejected.match(thunkResult)) {
            throw new Error(thunkResult.payload?.message ?? 'Internal error')
          }

          const usernameUnavailable = !thunkResult.payload
          if (usernameUnavailable) {
            validation.username = texts.usernameNonUnique
          }
        } catch (error) {
          console.error('Username validation error:', error)
        }
      }
    }

    return validation
  }

export const useValidateForm = (currentUserName: string) => {
  const texts = useTexts()
  const validateEmail = useValidateEmail()

  return async (formValues: RmAccountFormValues) => {
    return validateForm(texts, validateEmail)(formValues, currentUserName)
  }
}
